import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';
import { IconFile, IconTrash, IconUpload } from '../../components/Icons';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () => axios.get(`/api/admin/products/${id}`).then(res => {
    setProduct(res.data);
    setForm({
      name: res.data.name || '',
      category_id: res.data.category_id || '',
      model_number: res.data.model_number || '',
      manufacturer: res.data.manufacturer || '',
      description: res.data.description || '',
      default_cycle_limit: res.data.default_cycle_limit || '',
      // Stored as a Float in the backend; the form input edits the
      // raw decimal string so the operator can type 0.15, 2.0, etc.
      // without React's number-input quirks.
      speed_mps: res.data.speed_mps != null ? String(res.data.speed_mps) : '',
      image_url: res.data.image_url || null,
      // Phase 5 — single flag: does this product need two Shellys?
      requires_paired_install: !!res.data.requires_paired_install,
    });
  });

  useEffect(() => {
    load();
    axios.get('/api/admin/categories').then(res => setCategories(res.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await axios.put(`/api/admin/products/${id}`, {
        ...form,
        category_id: form.category_id || null,
        default_cycle_limit: form.default_cycle_limit ? parseInt(form.default_cycle_limit) : null,
        // Backend accepts string and parses, but normalise here too
        // so blank input clears the value cleanly.
        speed_mps: form.speed_mps === '' ? null : form.speed_mps,
      });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'שמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`למחוק את המוצר "${product.name}"?`)) return;
    try {
      await axios.delete(`/api/admin/products/${id}`);
      navigate('/products');
    } catch (err) {
      alert(err.response?.data?.error || 'מחיקה נכשלה');
    }
  };

  if (!product || !form) return <div className="loading">טוען...</div>;

  return (
    <>
      <div className="breadcrumb">
        <Link to="/products">מוצרים</Link>
        <span>/</span>
        <span>{product.name}</span>
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div className="info-card" style={{ flex: 1, minWidth: 0 }}>
          <div className="section-header">
            <h3>פרטי המוצר</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'שומר...' : 'שמור שינויים'}
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>מחק מוצר</button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ width: '160px' }}>
              <ImageUpload
                type="images"
                label="תמונת המוצר"
                value={form.image_url}
                onChange={url => setForm({ ...form, image_url: url })}
                height={160}
              />
            </div>

            <div style={{ flex: 1 }}>
              <div className="form-group">
                <label>שם המוצר</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>קטגוריה</label>
                <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">ללא קטגוריה</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>מספר דגם</label>
                  <input value={form.model_number} onChange={e => setForm({ ...form, model_number: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>יצרן</label>
                  <input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>ברירת מחדל - סייקלים לטיפול</label>
                  <input type="number" value={form.default_cycle_limit} onChange={e => setForm({ ...form, default_cycle_limit: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label title="מהירות תנועה לפי מפרט היצרן. משמש לחישוב אוטומטי של זמן הפתיחה/סגירה הצפוי לכל שער מהמודל הזה, יחד עם גובה הפתח שהטכנאי מזין בשטח. השאר ריק אם המפרט לא ידוע — המערכת תיפול חזרה לברירת מחדל של 45 שניות.">
                    מהירות (מטרים לשנייה)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.speed_mps}
                    onChange={e => setForm({ ...form, speed_mps: e.target.value })}
                    placeholder="לדוגמה: 0.2"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>תיאור</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={4}
              placeholder="תיאור המוצר, שימושים, הערות..."
              style={{
                width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
                border: '1px solid #e5e7eb', fontFamily: 'inherit', fontSize: '0.9rem',
                resize: 'vertical',
              }}
            />
          </div>

          <div className="form-group">
            <label>שיטת התקנה</label>
            <div style={{
              background: '#F8FAFC', borderRadius: '8px',
              padding: '0.85rem 1rem', border: '1px solid #E2E8F0',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.requires_paired_install}
                  onChange={e => setForm({ ...form, requires_paired_install: e.target.checked })}
                />
                <span><strong>דורש זוג רכיבי Shelly</strong> — רכיב אחד לפתיחה/סגירה (2PM) ורכיב נוסף לנעילה (על מעגל RSK). כברירת מחדל מוצרים מתקבלים עם רכיב יחיד; סמן רק עבור דגמים שדורשים שני רכיבים פיזיים.</span>
              </label>
            </div>
          </div>

          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            משויך ל-{product.gates_count} שערים במערכת.
          </div>

          {error && <div style={{ color: '#dc2626', marginTop: '0.5rem' }}>{error}</div>}
        </div>
      </div>

      <div className="info-card" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h3>מסמכים ופרוטוקולים</h3>
          <DocumentUploadButton productId={id} onUploaded={load} />
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
          המסמכים האלו חסויים לטכנאים של Huri בלבד - לקוחות לא רואים אותם.
        </p>

        {product.documents.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין עדיין מסמכים. לחץ "העלאת מסמך" כדי להוסיף.</p>
        ) : (
          <div className="doc-list">
            {product.documents.map(d => (
              <DocumentRow key={d.id} doc={d} onDeleted={load} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function DocumentUploadButton({ productId, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`/api/admin/products/${productId}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (err) {
      alert(err.response?.data?.error || 'העלאה נכשלה');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        <IconUpload size={14} /> {uploading ? 'מעלה...' : 'העלאת מסמך'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0])}
      />
    </>
  );
}

function DocumentRow({ doc, onDeleted }) {
  const handleDelete = async () => {
    if (!window.confirm(`למחוק את "${doc.name}"?`)) return;
    try {
      await axios.delete(`/api/admin/documents/${doc.id}`);
      onDeleted();
    } catch (err) {
      alert('מחיקה נכשלה');
    }
  };

  const sizeKB = doc.size ? Math.round(doc.size / 1024) : 0;

  return (
    <div className="doc-row">
      <div className="doc-row-icon"><IconFile size={20} /></div>
      <div className="doc-row-main">
        <a href={doc.url} target="_blank" rel="noreferrer" className="doc-row-name">
          {doc.name || doc.original_filename}
        </a>
        <div className="doc-row-meta">
          {doc.original_filename} · {sizeKB} KB · {new Date(doc.uploaded_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}
        </div>
      </div>
      <button className="btn btn-secondary btn-sm" onClick={handleDelete} title="מחק">
        <IconTrash size={14} />
      </button>
    </div>
  );
}

export default ProductDetail;
